import React, { useRef, useState } from 'react';
import { uploadAvatar } from '../services/api';
import toast from 'react-hot-toast';
import './AvatarUpload.css';

/**
 * AvatarUpload
 *
 * Props:
 *  currentUrl    — existing avatar URL (string or null)
 *  name          — user name (for initials fallback)
 *  onUpload      — (url: string) => void — called after successful upload
 *  size          — 'sm' | 'md' | 'lg' (default 'md')
 */
export default function AvatarUpload({ currentUrl, name = '', onUpload, size = 'md' }) {
  const inputRef   = useRef(null);
  const [preview,  setPreview]  = useState(currentUrl || null);
  const [loading,  setLoading]  = useState(false);

  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '؟';

  const handleFile = async (file) => {
    if (!file) return;

    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await uploadAvatar(fd);
      setPreview(res.data.url);
      onUpload?.(res.data.url);
      toast.success('✅ تم تحديث صورتك الشخصية!');
    } catch (err) {
      // Revert preview on error
      setPreview(currentUrl || null);
      toast.error(err.response?.data?.error || 'فشل رفع الصورة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`avatar-upload au-${size}`}>
      <div
        className="au-circle"
        onClick={() => !loading && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        title="اضغط لتغيير الصورة"
      >
        {preview ? (
          <img src={preview} alt={name} className="au-img" />
        ) : (
          <span className="au-initials">{initials}</span>
        )}

        {/* Overlay on hover */}
        <div className="au-overlay">
          {loading
            ? <div className="au-spinner" />
            : <span className="au-camera">📷</span>}
        </div>
      </div>

      <div className="au-label">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
        >
          {loading ? 'جاري الرفع...' : '📷 تغيير الصورة'}
        </button>
        <p className="au-hint">JPG أو PNG — حتى 5 ميجا</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
      />
    </div>
  );
}
